import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useWorkspace } from '@/providers/workspace-provider'
import { useAuth } from '@/providers/auth-provider'
import {
  ATTACHMENTS_BUCKET,
  buildAttachmentPath,
  removeAttachmentObject,
  validateAttachment,
} from '@/lib/storage'
import type { Tables } from '@/types/database'

export function useAttachments(taskId: string | null) {
  return useQuery({
    queryKey: ['attachments', taskId ?? 'none'],
    enabled: !!taskId,
    queryFn: async (): Promise<Tables<'attachments'>[]> => {
      const { data, error } = await supabase
        .from('attachments')
        .select('*')
        .eq('task_id', taskId!)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useUploadAttachment(taskId: string) {
  const { workspaceId } = useWorkspace()
  const { user } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      if (!workspaceId) throw new Error('Nenhum workspace selecionado')
      const invalid = validateAttachment(file)
      if (invalid) throw new Error(invalid)

      const path = buildAttachmentPath(workspaceId, taskId, file.name)
      const { error: upErr } = await supabase.storage.from(ATTACHMENTS_BUCKET).upload(path, file)
      if (upErr) throw upErr

      const { error } = await supabase.from('attachments').insert({
        workspace_id: workspaceId,
        task_id: taskId,
        uploaded_by: user?.id ?? null,
        file_name: file.name,
        storage_path: path,
        mime_type: file.type,
        size_bytes: file.size,
      })
      if (error) {
        // rollback do objeto se o registro falhar
        await removeAttachmentObject(path)
        throw error
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', taskId] }),
  })
}

export function useDeleteAttachment(taskId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (att: Tables<'attachments'>) => {
      await removeAttachmentObject(att.storage_path)
      const { error } = await supabase.from('attachments').delete().eq('id', att.id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', taskId] }),
  })
}
